// يولّد صورة الفاتورة + ملف PDF (صورة الفاتورة + صفحة لكل لوحة) لفاتورة
// محفوظة، بنفس طريقة حفظ الفاتورة بـInvoicePage.jsx بالضبط - يرسم نفس
// InvoicePreview خارج الشاشة ويصوّره. يُستخدم لفواتير "طلب جديد" مال ارت تاتش
// حتى تطلع بواجهة الفواتير مثل باقي الفواتير من أول لحظة.
import React from "react";
import { createRoot } from "react-dom/client";
import { flushSync } from "react-dom";
import html2canvas from "html2canvas";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../firebase";
import { InvoicePreview, buildMultiPagePdf, urlToDataUrl } from "../InvoicePage";

const waitForImages = (el) =>
  Promise.all(
    Array.from(el.querySelectorAll("img")).map((img) =>
      img.complete
        ? null
        : new Promise((resolve) => {
            img.onload = resolve;
            img.onerror = resolve;
          })
    )
  );

const uploadBlob = async (path, blob) => {
  const fileRef = ref(storage, path);
  await uploadBytes(fileRef, blob);
  return getDownloadURL(fileRef);
};

// يرسم InvoicePreview خارج الشاشة ويرجّع صورة الفاتورة (canvas) + ملف PDF
// (صورة الفاتورة + صفحة لكل لوحة) - مشترك بين الرفع والتنزيل على الجهاز.
// صور اللوحات تُقرأ من img.url (رابط مرفوع، أو رابط معاينة blob: قبل الرفع)
const renderInvoicePdf = async (invoiceData) => {
  const host = document.createElement("div");
  host.style.cssText = "position:fixed;left:-10000px;top:0;pointer-events:none;";
  document.body.appendChild(host);
  const root = createRoot(host);

  try {
    const previewRef = { current: null };
    flushSync(() => {
      root.render(<InvoicePreview ref={previewRef} invoice={invoiceData} />);
    });
    await waitForImages(host);

    const canvas = await html2canvas(previewRef.current, { scale: 2, useCORS: true });

    const itemsWithImageData = await Promise.all(
      (invoiceData.items || []).map(async (item) => {
        const imageDataUrls = await Promise.all(
          (item.panelImages || []).map(async (img) => {
            try {
              return img.url ? await urlToDataUrl(img.url) : null;
            } catch (err) {
              console.error("تعذر تحميل صورة اللوحة للـPDF:", err);
              return null;
            }
          })
        );
        return { item, imageDataUrls: imageDataUrls.filter(Boolean) };
      })
    );

    // صفحة الفاتورة JPEG بدل PNG: ضغط PNG بـjsPDF بطيء جداً والملف يطلع
    // ~13MB، فكان حفظ الطلب ياخذ وقت طويل (خصوصاً بالموبايل)
    const pdf = await buildMultiPagePdf(canvas, itemsWithImageData, invoiceData.panelTypeLabels, "JPEG");
    return { canvas, pdf };
  } finally {
    root.unmount();
    host.remove();
  }
};

// invoiceData: بيانات النسخة (نفس شكل version.data). storageFolder: مجلد الرفع
export const generateInvoiceAssets = async (invoiceData, storageFolder) => {
  // نتحقق قبل الرسم: أخطاء الرسم داخل createRoot ما ترجع للمستدعي (تطلع
  // كخطأ عام يوقف الصفحة)، فبيانات ناقصة لازم ترفض هنا كخطأ عادي يُلتقط
  if (!invoiceData || !invoiceData.invoiceNumber || !Array.isArray(invoiceData.items)) {
    throw new Error("Missing invoice data for asset generation");
  }

  const { canvas, pdf } = await renderInvoicePdf(invoiceData);
  const pngBlob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
  const stamp = Date.now();
  // الصورة والـPDF ينرفعون سوا بدل وحدة بعد الثانية
  const [imageUrl, pdfUrl] = await Promise.all([
    uploadBlob(`${storageFolder}/invoice-${invoiceData.invoiceNumber}-${stamp}.png`, pngBlob),
    uploadBlob(`${storageFolder}/invoice-${invoiceData.invoiceNumber}-${stamp}.pdf`, pdf.output("blob")),
  ]);

  return { imageUrl, pdfUrl };
};

// 📥 ينزّل ملف PDF الفاتورة على جهاز المستخدم فقط (بدون أي رفع أو حفظ
// بالسيرفر) - تُستخدم بفورم ارت تاتش قبل حفظ الطلب
export const downloadInvoicePdf = async (invoiceData, fileName) => {
  if (!invoiceData || !Array.isArray(invoiceData.items)) {
    throw new Error("Missing invoice data for PDF download");
  }
  const { pdf } = await renderInvoicePdf(invoiceData);
  pdf.save(fileName);
};
